import React, { useState } from "react";
import { Loader2 } from "lucide-react";

import { AppLogo } from "@/shared/components/AppLogo";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { BrandButton, Chip, GhostButton } from "@/shared/components/ui-kit";
import { useConnectApp, useConnections, useLinkConnection, useSetAppSource } from "@/shared/api/apps";

import type { ConnectionAppRef } from "@/shared/types";

/* Whose account an app on the agent uses: one of the owner's (included for everyone who
   talks to the agent) or each customer's own (they connect it on the agent's page the
   first time it's needed). Choosing one replaces the other on the server, so this is how
   an app is added and how it is switched later. */

type Whose = "owner" | "customer";

const CHOICES: { value: Whose; title: string; desc: string }[] = [
  { value: "owner", title: "My account", desc: "Included for everyone who talks to the agent. Its usage runs on your account." },
  { value: "customer", title: "Each customer's own", desc: "Each customer connects theirs on the agent's page, the first time it's needed." },
];

export function WhoseAccount({ agentId, app, now, triggers = 0, customers = 0, onDone }: {
  agentId: string;
  app: ConnectionAppRef;
  /** What the app uses on this agent today; unset while it is being added. */
  now?: Whose;
  /** The agent's triggers on the owner's accounts of the app: they go with them. */
  triggers?: number;
  /** Customers who connected their own: those accounts go when the owner's replaces them. */
  customers?: number;
  onDone: () => void;
}) {
  const [whose, setWhose] = useState<Whose>(now === "owner" ? "customer" : "owner");
  const { data: connections } = useConnections();
  const link = useLinkConnection();
  const setSource = useSetAppSource();
  const { connect, busySlug } = useConnectApp();
  const mine = (connections ?? []).filter((c) => c.app.slug === app.slug && c.status === "connected");
  const busy = link.isPending || setSource.isPending || !!busySlug;

  const use = (connectionId: string) => link.mutate({ id: connectionId, body: { agent_id: agentId } }, { onSuccess: onDone });
  // The popup has to open inside the click, so `connect` goes first and the link waits.
  const connectNew = async () => {
    const connection = await connect(app.slug);
    if (connection) use(connection.id);
  };

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label={`Whose account ${app.name} uses`} className="space-y-2">
        {CHOICES.map((choice) => (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={whose === choice.value}
            disabled={now === choice.value}
            onClick={() => setWhose(choice.value)}
            className={`press w-full flex items-start gap-3 rounded-[10px] px-3 py-2.5 text-left ring-1 disabled:cursor-default ${
              whose === choice.value ? "ring-[var(--hairline-focus)] bg-[var(--surface)]" : "ring-[var(--hairline)] hover:bg-[var(--accent-hover)]"
            }`}
          >
            <span className="mt-[3px] w-3.5 h-3.5 shrink-0 rounded-full ring-1 ring-[var(--hairline-strong)] flex items-center justify-center">
              {whose === choice.value && <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-primary)]" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-[13px] font-medium text-[var(--text-primary)]">
                {choice.title}
                {now === choice.value && <Chip>In use</Chip>}
              </span>
              <span className="block mt-0.5 text-[12px] leading-[1.5] text-[var(--text-tertiary)]">{choice.desc}</span>
            </span>
          </button>
        ))}
      </div>

      {whose === "owner" && now !== "owner" && (
        <div className="rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] overflow-hidden divide-y divide-[var(--hairline)]">
          {mine.map((c) => (
            <div key={c.id} className="flex items-center gap-2.5 px-3 py-2 min-w-0">
              <AppLogo logo={c.app.logo} name={c.app.name} size={22} iconSize={13} rounded={6} />
              <span className="flex-1 min-w-0 truncate text-[13px] text-[var(--text-primary)]">{c.account_label ?? c.app.name}</span>
              <GhostButton className="h-7 px-2.5 disabled:opacity-40 disabled:pointer-events-none" disabled={busy} onClick={() => use(c.id)}>
                Use
              </GhostButton>
            </div>
          ))}
          <button
            type="button"
            disabled={busy}
            onClick={connectNew}
            className="press w-full flex items-center justify-center gap-1.5 h-9 text-[12.5px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] disabled:opacity-40"
          >
            {busySlug ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} /> : null}
            {mine.length ? `Connect another ${app.name} account` : `Connect your ${app.name} account`}
          </button>
        </div>
      )}
      {whose === "customer" && now !== "customer" && (
        <BrandButton className="w-full disabled:opacity-40" disabled={busy} onClick={() => setSource.mutate({ slug: app.slug, agentId, account: "customer" }, { onSuccess: onDone })}>
          Use each customer's own
        </BrandButton>
      )}

      {now === "owner" && whose === "customer" && (
        <p className="m-0 text-[12px] leading-[1.5] text-[var(--text-tertiary)]">
          Your {app.name} account stays connected, off this agent{triggers > 0 && `, and its ${triggers === 1 ? "trigger here is" : `${triggers} triggers here are`} deleted`}.
        </p>
      )}
      {now === "customer" && whose === "owner" && customers > 0 && (
        <p className="m-0 text-[12px] leading-[1.5] text-[var(--text-tertiary)]">
          The {customers === 1 ? "customer" : `${customers} customers`} who connected their own {app.name} account {customers === 1 ? "is" : "are"} disconnected from it.
        </p>
      )}
    </div>
  );
}

/** Switching an app already on the agent. */
export function WhoseAccountDialog({ onOpenChange, ...props }: Omit<React.ComponentProps<typeof WhoseAccount>, "onDone"> & {
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-[440px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-5 space-y-3">
          <div>
            <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
              Whose {props.app.name} account?
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">
              The agent keeps calling it by the same name either way.
            </DialogDescription>
          </div>
          <WhoseAccount {...props} onDone={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
